import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
  orgSlug: string;
  projectSlug: string;
  rowId: string;
  onAssigned?: () => void;
  /** Defaults if reassigning from a live row. */
  initial?: { targetType: TargetType; targetBranch: string };
  /** Submit label override. */
  submitLabel?: string;
}

type TargetType = "production" | "staging" | "development" | "branch";

interface DeploymentRow {
  branch: string;
}

/**
 * Step 5 — pick env / branch and confirm. Used both by the wizard's
 * first-time assign (assigning → live) and the reassign-on-live dialog
 * that lands in Phase 5 (live → live, with a parent-supplied confirm UX).
 *
 * The toggle uses the platform's `accent` token for selected state. `primary`
 * is not a Tailwind color in this theme — early versions used it and the
 * selection state was invisible.
 *
 * The Branch field auto-completes from the project's recent deployment
 * branches via the same `/deployments?limit=50` query DeployHooks.tsx uses
 * to seed its branch picker.
 */
export function AssignTarget({
  orgSlug,
  projectSlug,
  rowId,
  onAssigned,
  initial,
  submitLabel = "Confirm",
}: Props) {
  const qc = useQueryClient();
  const [targetType, setTargetType] = useState<TargetType>(initial?.targetType ?? "production");
  const [targetBranch, setTargetBranch] = useState(initial?.targetBranch ?? "");

  // Branch suggestions sourced from the project's recent deployments.
  const deployments = useQuery({
    enabled: targetType === "branch",
    queryKey: ["deployments", orgSlug, projectSlug, "branches"],
    queryFn: () =>
      api
        .get<{ deployments: DeploymentRow[] }>(
          `/orgs/${orgSlug}/projects/${projectSlug}/deployments?limit=50`,
        )
        .then((r) => r.deployments),
  });
  const knownBranches = useMemo(() => {
    const s = new Set<string>();
    for (const d of deployments.data ?? []) s.add(d.branch);
    return Array.from(s).sort();
  }, [deployments.data]);

  const patch = useMutation<unknown, ApiError>({
    mutationFn: () =>
      api.patch(
        `/orgs/${orgSlug}/projects/${projectSlug}/domains/${rowId}/target`,
        targetType === "branch"
          ? { targetType, targetBranch }
          : { targetType },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["project-domains", orgSlug, projectSlug] });
      onAssigned?.();
    },
  });

  const disabled =
    patch.isPending || (targetType === "branch" && !targetBranch.trim());

  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <Label className="text-xs">Route traffic to</Label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-xs">
          {(["production", "staging", "development", "branch"] as const).map((t) => {
            const selected = targetType === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setTargetType(t)}
                aria-pressed={selected}
                className={
                  "rounded-md border px-2 py-1.5 font-mono transition-colors " +
                  (selected
                    ? "bg-accent text-white border-accent shadow-sm"
                    : "border-border-muted bg-background hover:bg-subtle hover:border-border")
                }
              >
                {t}
              </button>
            );
          })}
        </div>
      </div>
      {targetType === "branch" && (
        <div className="space-y-1">
          <Label htmlFor="target-branch" className="text-xs">
            Branch{knownBranches.length > 0 && (
              <span className="text-2xs text-subtle-foreground font-normal ml-1">
                ({knownBranches.length} known)
              </span>
            )}
          </Label>
          <Input
            id="target-branch"
            value={targetBranch}
            onChange={(e) => setTargetBranch(e.target.value)}
            placeholder="feature-x"
            list="assign-target-branches"
            autoComplete="off"
            className="font-mono text-sm"
          />
          <datalist id="assign-target-branches">
            {knownBranches.map((b) => (
              <option key={b} value={b} />
            ))}
          </datalist>
          {deployments.isLoading && (
            <div className="text-2xs text-subtle-foreground">Loading branch suggestions…</div>
          )}
        </div>
      )}
      {patch.error && (
        <div className="text-xs text-danger">{patch.error.message}</div>
      )}
      <div className="flex justify-end">
        <Button
          variant="primary"
          size="sm"
          disabled={disabled}
          onClick={() => patch.mutate()}
        >
          {patch.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
